import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import { format } from "date-fns";
import PageTitle from "components/PageTitle/PageTitle";
import CustomMUIDataTable from "components/MUIDatatable/CustomMUIDataTablev2";
import useStyles from "../BatchReports/styles";

const INITIAL_REQUESTS = [
  {
    id: 1,
    status: "PENDIENTE",
    requestedAt: "2026-08-13T10:45:00-03:00",
    username: "maria.gonzalez",
    fullName: "María González",
    currentEmail: "maria.gonzalez@correo.com",
    newEmail: "maria.gonzalez@empresa.com",
    phone: "+56 9 8765 4321",
    comments:
      "Solicito el cambio porque ya no tengo acceso a mi correo anterior.",
  },
  {
    id: 2,
    status: "RECHAZADA",
    requestedAt: "2026-08-12T16:20:00-03:00",
    username: "juan.perez",
    fullName: "Juan Pérez",
    currentEmail: "juan.perez@correo.com",
    newEmail: "jperez@empresa.com",
    phone: "+56 9 1234 5678",
    comments: "Mensaje de ejemplo cargado desde el formulario de solicitud.",
  },
];

const REQUEST_STATE_STYLES = {
  APROBADA: { backgroundColor: "#C8E6C9", color: "#1B5E20" },
  RECHAZADA: { backgroundColor: "#FFCDD2", color: "#B71C1C" },
  PENDIENTE: { backgroundColor: "#BBDEFB", color: "#0D47A1" },
};

const REQUEST_STATE_LABELS = {
  APROBADA: "Aprobada",
  RECHAZADA: "Rechazada",
  PENDIENTE: "Pendiente",
};

const formatRequestDate = (date) => format(new Date(date), "dd/MM/yyyy HH:mm");

function RequestStateChip({ status }) {
  const style = REQUEST_STATE_STYLES[status] || REQUEST_STATE_STYLES.PENDIENTE;

  return (
    <Chip
      label={REQUEST_STATE_LABELS[status] || REQUEST_STATE_LABELS.PENDIENTE}
      size="small"
      style={{ ...style, fontWeight: 700, height: 24 }}
    />
  );
}

function RequestDetails({ request }) {
  const details = [
    ["Estado", <RequestStateChip status={request.status} />],
    ["Fecha", formatRequestDate(request.requestedAt)],
    ["Nombre de usuario", request.username],
    ["Nombre y apellido", request.fullName],
    ["Email actual", request.currentEmail],
    ["Email nuevo", request.newEmail],
    ["Teléfono", request.phone],
  ];

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "180px minmax(0, 1fr)",
          rowGap: 12,
          columnGap: 16,
          padding: 16,
          border: "1px solid rgba(0, 0, 0, 0.08)",
          borderRadius: 12,
          backgroundColor: "#fafafa",
        }}
      >
        {details.map(([label, value]) => (
          <React.Fragment key={label}>
            <Typography
              variant="body2"
              style={{
                alignSelf: "center",
                color: "rgba(0, 0, 0, 0.62)",
                fontWeight: 700,
                textAlign: "right",
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="body2"
              style={{
                alignItems: "center",
                display: "flex",
                minHeight: 24,
                minWidth: 0,
              }}
            >
              {value}
            </Typography>
          </React.Fragment>
        ))}
      </div>

      <TextField
        fullWidth
        multiline
        rows={4}
        label="Comentarios"
        value={request.comments}
        variant="outlined"
        InputProps={{ readOnly: true }}
        style={{ marginTop: 24 }}
      />
    </>
  );
}

export default function EmailRequestPage() {
  const classes = useStyles();
  const [requests, setRequests] = useState(INITIAL_REQUESTS);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const selectedRequest = requests.find(
    (request) => request.id === selectedRequestId,
  );

  const sortedRequests = useMemo(
    () =>
      [...requests].sort(
        (a, b) => new Date(b.requestedAt) - new Date(a.requestedAt),
      ),
    [requests],
  );

  const visibleRequests = useMemo(
    () =>
      showPendingOnly
        ? sortedRequests.filter((request) => request.status === "PENDIENTE")
        : sortedRequests,
    [showPendingOnly, sortedRequests],
  );

  const closeDetails = () => setSelectedRequestId(null);

  const updateRequestStatus = (status) => {
    setRequests((currentRequests) =>
      currentRequests.map((request) =>
        request.id === selectedRequestId ? { ...request, status } : request,
      ),
    );
    closeDetails();
  };

  const columns = useMemo(
    () => [
      {
        name: "Detalle",
        options: {
          sort: false,
          setCellHeaderProps: () => ({
            style: { whiteSpace: "nowrap", width: 90 },
          }),
          setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        },
      },
      {
        name: "Estado",
        options: {
          sort: false,
          setCellHeaderProps: () => ({
            style: { whiteSpace: "nowrap", minWidth: 180 },
          }),
        },
      },
      {
        name: "Fecha",
        options: {
          sort: false,
          setCellHeaderProps: () => ({
            style: { whiteSpace: "nowrap", minWidth: 170 },
          }),
        },
      },
      {
        name: "Nombre de usuario",
        options: {
          sort: false,
          setCellHeaderProps: () => ({
            style: { whiteSpace: "nowrap", minWidth: 220 },
          }),
        },
      },
      {
        name: "Nombre y apellido",
        options: {
          sort: false,
          setCellHeaderProps: () => ({
            style: { whiteSpace: "nowrap", minWidth: 230 },
          }),
        },
      },
      {
        name: "Email actual",
        options: {
          sort: false,
          setCellHeaderProps: () => ({
            style: { whiteSpace: "nowrap", minWidth: 230 },
          }),
        },
      },
      {
        name: "Email nuevo",
        options: {
          sort: false,
          setCellHeaderProps: () => ({
            style: { whiteSpace: "nowrap", minWidth: 220 },
          }),
        },
      },
    ],
    [],
  );

  const tableData = visibleRequests.map((request) => [
    <Tooltip title="Ver detalle" key={`detail-${request.id}`}>
      <IconButton
        size="small"
        aria-label="Ver detalle"
        onClick={() => setSelectedRequestId(request.id)}
      >
        <ContentPasteSearchIcon fontSize="small" />
      </IconButton>
    </Tooltip>,
    <RequestStateChip key={`status-${request.id}`} status={request.status} />,
    formatRequestDate(request.requestedAt),
    request.username,
    request.fullName,
    request.currentEmail,
    request.newEmail,
  ]);

  return (
    <Grid>
      <PageTitle title="Solicitudes de cambio de correo" />

      <Paper elevation={3} className={classes.paperContainer}>
        <Grid
          container
          direction="column"
          spacing={3}
          style={{ padding: 16, background: "#f5f5f5" }}
        >
          <Grid item xs={12} style={{ marginTop: 16, overflowX: "auto" }}>
            <CustomMUIDataTable
              title="Listado de solicitudes"
              data={tableData}
              columns={columns}
              count={visibleRequests.length}
              page={0}
              totalPages={1}
              rowsPerPage={5}
              changePage={() => {}}
              changeRowsPerPage={() => {}}
              options={{
                pagination: false,
                filter: false,
                search: false,
                download: false,
                customToolbar: () => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPendingOnly}
                        color="primary"
                        onChange={(event) =>
                          setShowPendingOnly(event.target.checked)
                        }
                        size="small"
                      />
                    }
                    label="Solo pendientes"
                    style={{ marginRight: 8, whiteSpace: "nowrap" }}
                  />
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={Boolean(selectedRequest)}
        onClose={closeDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalle</DialogTitle>
        <DialogContent dividers>
          {selectedRequest && <RequestDetails request={selectedRequest} />}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => updateRequestStatus("RECHAZADA")}
            color="secondary"
          >
            Rechazar
          </Button>
          <Button
            onClick={() => updateRequestStatus("APROBADA")}
            color="primary"
            variant="contained"
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
